import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const UPDATED = "June 2026";
const CONTACT = "info@vs-solutions.org";
const SITE = "https://bitego.org";
const COMPANY = "Vijay Software Solutions Pvt Ltd";
const CEO = "Perikala Bhargav";

function LegalShell({ title, children }) {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-10 bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <button onClick={() => nav(-1)} className="w-9 h-9 -ml-1 flex items-center justify-center text-ink hover:text-coral transition-colors tap" aria-label="Back"><ArrowLeft size={22} /></button>
          <img src="/bitego-icon.png" alt="BiteGoFood" className="w-8 h-8 rounded-lg" />
          <span className="font-display font-extrabold text-lg text-ink">BiteGoFood</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-ink tracking-tight">{title}</h1>
        <p className="text-sm text-ink-600 mt-2">Last updated: {UPDATED}</p>
        <div className="mt-6 space-y-6 text-[15px] leading-relaxed text-ink">{children}</div>
        <footer className="mt-12 pt-6 border-t border-border text-sm text-ink-600">
          <p>{COMPANY} · Founder &amp; CEO: {CEO}</p>
          <p className="mt-1">Website: <a href={SITE} className="text-coral font-semibold">{SITE}</a> · Contact: <a href={`mailto:${CONTACT}`} className="text-coral font-semibold">{CONTACT}</a></p>
        </footer>
      </main>
    </div>
  );
}

const Sec = ({ h, children }) => (<section><h2 className="font-display font-bold text-lg text-ink mb-2">{h}</h2><div className="space-y-2 text-ink-600">{children}</div></section>);

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy">
      <p>BiteGoFood ("we", "our", "us"), operated by {COMPANY}, provides an online food ordering and delivery platform at {SITE}. This policy explains what data we collect and how we use it.</p>
      <Sec h="Information we collect">
        <p>• Account details: name, mobile number, email, and preferred language.</p>
        <p>• Delivery information: saved addresses and, with your permission, device location to find nearby restaurants and enable delivery and live tracking.</p>
        <p>• Order and transaction data: cart contents, order history, and payment status. Card/UPI details are processed by our payment provider (Razorpay) and are not stored on our servers.</p>
        <p>• Partner data (restaurants/delivery partners): business/KYC documents submitted for onboarding and verification.</p>
        <p>• Technical data: device information, app interactions, and notification tokens used to deliver order and account updates.</p>
      </Sec>
      <Sec h="How we use your information">
        <p>To create and secure your account, process and deliver orders, take payments and issue refunds, provide order tracking, send transactional notifications and emails, verify partners, prevent fraud, and improve the service.</p>
      </Sec>
      <Sec h="Sharing">
        <p>We share only what is necessary to fulfil your order — with the selected restaurant, the assigned delivery partner, and our payment provider. We do not sell your personal data.</p>
      </Sec>
      <Sec h="Data security & retention">
        <p>We use authentication, role-based access controls and encryption in transit. We retain data only as long as needed for the service and legal/accounting requirements.</p>
      </Sec>
      <Sec h="Your choices">
        <p>You can update your profile and addresses, manage notification permissions on your device, and request account or data deletion by emailing {CONTACT}.</p>
      </Sec>
      <Sec h="Permissions used by the app">
        <p>Location (nearby restaurants & delivery), Notifications (order/account updates), Camera & Storage (uploading documents/photos where required), and links to phone, email and maps. Permissions are requested only when needed.</p>
      </Sec>
      <Sec h="Contact">
        <p>Questions? Email {CONTACT}. Replies to our emails go to this address.</p>
      </Sec>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms & Conditions">
      <p>These terms govern your use of BiteGoFood, operated by {COMPANY}, available at {SITE}.</p>
      <Sec h="Using BiteGoFood">
        <p>You must provide accurate account information and verify your mobile number via OTP. You are responsible for activity under your account. Restaurant and delivery partners must complete onboarding and verification before operating.</p>
      </Sec>
      <Sec h="Orders & pricing">
        <p>Prices are set by restaurants. The amount you pay is calculated as Food Cost + Delivery Fee − Discount. Menu availability and delivery times are estimates and may vary.</p>
      </Sec>
      <Sec h="Payments & refunds">
        <p>Payments are processed securely by our payment provider. Eligible refunds are issued to your original payment method or wallet per our refund policy. Cash on Delivery may be available where supported.</p>
      </Sec>
      <Sec h="Cancellations">
        <p>Orders may be cancelled subject to preparation status. Once a restaurant begins preparing an order, cancellation may not be possible.</p>
      </Sec>
      <Sec h="Acceptable use">
        <p>Do not misuse the platform, attempt unauthorized access, or interfere with its operation. Admin systems are restricted and access is prohibited without authorization.</p>
      </Sec>
      <Sec h="Liability">
        <p>The service is provided "as is". To the extent permitted by law, {COMPANY} is not liable for indirect or incidental damages arising from use of the platform.</p>
      </Sec>
      <Sec h="Changes & contact">
        <p>We may update these terms; continued use constitutes acceptance. Questions? Email {CONTACT}.</p>
      </Sec>
    </LegalShell>
  );
}
